/** 集成弹窗内的服务商并发槽数设置；与凭据分开保存，修改后从下一次认领起生效。 */
import {
  LANDING_AI_PROVIDER_CONCURRENCY_MAX,
  type LandingAiProvider,
  UpdateLandingAiProviderConcurrencyRequestSchema,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLandingAiProviderConcurrency } from '~/data/queries';
import { landingAiProviderConcurrencyApi } from '~/hooks/use-landing-ai-provider-concurrency';
import { FormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

export function ProviderConcurrencyField({
  provider,
  label,
}: {
  provider: LandingAiProvider;
  label: string;
}) {
  const q = useLandingAiProviderConcurrency();
  const current = q.data?.[provider];
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  useEffect(() => {
    if (current !== undefined) setValue(String(current));
  }, [current]);
  const save = useMutation({
    mutationFn: (max_concurrency: number) =>
      landingAiProviderConcurrencyApi.update({ provider, max_concurrency }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['model-configurations', 'provider-concurrency'] });
      toast(`${label} 并发数已保存`, { tone: 'pos' });
    },
    onError: (e) => setError(e.message),
  });
  const submit = () => {
    const result = UpdateLandingAiProviderConcurrencyRequestSchema.safeParse({
      provider,
      max_concurrency: Number(value),
    });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? '并发数错误');
      return;
    }
    setError('');
    save.mutate(result.data.max_concurrency);
  };
  return (
    <div style={{ marginTop: 16 }}>
      <FormRow
        label="并发数"
        hint={`同一服务商同时执行的章节处理路数（1–${LANDING_AI_PROVIDER_CONCURRENCY_MAX}）；超过云端套餐并发或本机并行能力会频繁限流、超时重试`}
      >
        <div className="hstack" style={{ gap: 8 }}>
          <input
            aria-label={`${label} 并发数`}
            className="input mono"
            type="number"
            min={1}
            max={LANDING_AI_PROVIDER_CONCURRENCY_MAX}
            step={1}
            value={value}
            disabled={q.isPending || save.isPending}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
          />
          <button
            className="btn"
            disabled={q.isPending || save.isPending || value === '' || value === String(current)}
            onClick={submit}
          >
            {save.isPending ? '保存中…' : '保存并发数'}
          </button>
        </div>
      </FormRow>
      {q.isError && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {q.error.message}
        </div>
      )}
      {error && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
